// frontend/src/pages/admin/AdminCoursesPage.tsx
// Kurs, ders ve proje yönetimi: is_premium, certificate_enabled, hackathon_enabled,
// is_preview, is_published flag'leri admin panelinden yönetilebilir.

import { useEffect, useState, useCallback, useRef } from 'react';
import { authedFetch, adminService } from '../../services/admin.service';
import { supabase } from '../../lib/supabaseClient';
import { LessonFormModal } from '../../components/admin/content/LessonFormModal';
import { LessonTopicsPanel } from '../../components/admin/content/LessonTopicsPanel';
import { ProjectFormModal } from '../../components/admin/content/ProjectFormModal';
import { AdminProject } from '../../types/admin.types';
import { Edit, Image, Book } from 'lucide-react';

interface Course {
  id: string; title: string; description: string | null;
  category: string; difficulty: string;
  is_published: boolean; is_premium: boolean;
  certificate_enabled: boolean; hackathon_enabled: boolean;
  min_age: number | null; max_age: number | null;
  age_category: string | null;
  cover_image_url: string | null;
}
interface Lesson {
  id: string; course_id: string; title: string;
  content: string | null; video_url: string | null;
  order_index: number; xp_reward: number;
  is_preview: boolean; is_premium: boolean;
  has_video: boolean; has_quiz: boolean; has_editor: boolean;
}

const DIFF_LABELS: Record<string, string> = { beginner: 'Başlangıç', intermediate: 'Orta', advanced: 'İleri' };
const CAT_LABELS: Record<string, string> = {
  python: 'Python', matematik: 'Matematik', math: 'Matematik', fen: 'Fen Bilimleri', science: 'Fen Bilimleri',
  ingilizce: 'İngilizce', english: 'İngilizce', turkce: 'Türkçe', turkish: 'Türkçe',
  web: 'Web', game: 'Oyun', veri_analizi: 'Veri Analizi', yapay_zeka: 'Yapay Zeka', coding: 'Kodlama'
};
const COURSE_CAT_LABELS: Record<string, string> = {
  python: 'Python',
  matematik: 'Matematik',
  fen: 'Fen Bilimleri',
  ingilizce: 'İngilizce',
  turkce: 'Türkçe',
  web: 'Web',
  game: 'Oyun',
  veri_analizi: 'Veri Analizi',
  yapay_zeka: 'Yapay Zeka'
};

type Tab = 'courses' | 'lessons' | 'projects';

export function AdminCoursesPage() {
  const [tab, setTab] = useState<Tab>('courses');
  const [courses, setCourses] = useState<Course[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseModalMode, setCourseModalMode] = useState<'create' | 'edit'>('create');
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [courseForm, setCourseForm] = useState({ title: '', description: '', category: 'python', difficulty: 'beginner', age_category: '', cover_image_url: '' });
  const [imgUploading, setImgUploading] = useState(false);
  const [imgUploadProgress, setImgUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ders ve Konu Modalları State'i
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [selectedLessonForEdit, setSelectedLessonForEdit] = useState<Lesson | null>(null);
  const [showTopicsPanel, setShowTopicsPanel] = useState(false);
  const [selectedLessonForTopics, setSelectedLessonForTopics] = useState<Lesson | null>(null);

  // Proje Modalı State'i
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProjectForEdit, setSelectedProjectForEdit] = useState<AdminProject | null>(null);

  const flash = (t: string) => { setMsg(t); setTimeout(() => setMsg(null), 2500); };

  const loadCourses = useCallback(async () => {
    setIsLoading(true);
    const d = await authedFetch<{ courses: Course[] }>('/api/admin/content/courses');
    setCourses(d.courses ?? []);
    setIsLoading(false);
  }, []);

  const loadLessons = useCallback(async (courseId: string) => {
    const d = await authedFetch<{ lessons: Lesson[] }>(`/api/admin/content/courses/${courseId}/lessons`);
    setLessons(d.lessons ?? []);
  }, []);

  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminService.listProjects();
      setProjects(data ?? []);
    } catch {
      flash('Projeler yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
    loadProjects();
  }, [loadCourses, loadProjects]);

  // Kurs flag toggle
  async function patchCourse(id: string, patch: Partial<Course>) {
    await authedFetch(`/api/admin/content/courses/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    loadCourses();
    flash('✅ Kurs güncellendi');
  }

  // Ders flag toggle
  async function patchLesson(id: string, patch: Partial<Lesson>) {
    await authedFetch(`/api/admin/content/lessons/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    if (selectedCourse) loadLessons(selectedCourse.id);
    flash('✅ Ders güncellendi');
  }

  // Proje flag toggle / update
  async function patchProject(id: string, patch: Partial<AdminProject>) {
    await adminService.updateProject(id, patch);
    loadProjects();
    flash('✅ Proje güncellendi');
  }

  async function deleteCourse(id: string, title: string) {
    if (!confirm(`"${title}" silinecek. Emin misiniz?`)) return;
    await authedFetch(`/api/admin/content/courses/${id}`, { method: 'DELETE' });
    loadCourses(); flash('✅ Kurs silindi');
  }

  async function deleteLesson(id: string, title: string) {
    if (!confirm(`"${title}" silinecek?`)) return;
    await authedFetch(`/api/admin/content/lessons/${id}`, { method: 'DELETE' });
    if (selectedCourse) loadLessons(selectedCourse.id);
    flash('✅ Ders silindi');
  }

  async function deleteProject(id: string, title: string) {
    if (!confirm(`"${title}" projesi silinecek. Emin misiniz?`)) return;
    await adminService.deleteProject(id);
    loadProjects(); flash('✅ Proje silindi');
  }

  async function handleCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    setImgUploadProgress(10);
    try {
      const ext = file.name.split('.').pop();
      const path = `course-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
      const { error: upErr } = await supabase.storage.from('course-covers').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      setImgUploadProgress(70);
      const { data: { publicUrl } } = supabase.storage.from('course-covers').getPublicUrl(path);
      setCourseForm(f => ({ ...f, cover_image_url: publicUrl }));
      setImgUploadProgress(100);
      setTimeout(() => setImgUploadProgress(0), 1500);
    } catch (err) {
      alert('Görsel yükleme başarısız: ' + (err instanceof Error ? err.message : String(err)));
      setImgUploadProgress(0);
    } finally {
      setImgUploading(false);
    }
  }

  async function saveCourse() {
    if (!courseForm.title.trim()) return;
    const payload: Record<string, unknown> = {
      title: courseForm.title,
      description: courseForm.description,
      category: courseForm.category,
      difficulty: courseForm.difficulty,
      age_category: courseForm.age_category || null,
    };
    if (courseForm.cover_image_url) payload.cover_image_url = courseForm.cover_image_url;
    if (courseModalMode === 'create') {
      await authedFetch('/api/admin/content/courses', { method: 'POST', body: JSON.stringify(payload) });
      flash('✅ Kurs oluşturuldu');
    } else {
      await authedFetch(`/api/admin/content/courses/${selectedCourseId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      flash('✅ Kurs güncellendi');
    }
    setShowCourseModal(false);
    loadCourses();
  }

  function openLessons(course: Course) {
    setSelectedCourse(course);
    setTab('lessons');
    loadLessons(course.id);
  }

  function handleEditLesson(lesson: Lesson) {
    setSelectedLessonForEdit(lesson);
    setShowLessonForm(true);
  }

  function handleNewLesson() {
    setSelectedLessonForEdit(null);
    setShowLessonForm(true);
  }

  function handleOpenTopics(lesson: Lesson) {
    setSelectedLessonForTopics(lesson);
    setShowTopicsPanel(true);
  }

  function handleNewProject() {
    setSelectedProjectForEdit(null);
    setShowProjectModal(true);
  }

  function handleEditProject(project: AdminProject) {
    setSelectedProjectForEdit(project);
    setShowProjectModal(true);
  }

  // ── RENDER ────────────────────────────────────────────────────────
  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>📚 Kurs, Ders & Proje Yönetimi</h1>
        <p>Kurslar, dersler, ders konuları ve kullanıcı projesi içeriklerini buradan yönetebilirsiniz.</p>
      </header>

      {msg && <div className="quiz-error-banner" style={{ background: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.3)', color: 'var(--secondary)', marginBottom: 16 }}>{msg}</div>}

      <div className="courses-tabs" style={{ marginBottom: 20, display: 'flex', gap: 8 }}>
        <button className={`courses-tab ${tab === 'courses' ? 'active' : ''}`} onClick={() => setTab('courses')}>
          Kurslar ({courses.length})
        </button>

        <button className={`courses-tab ${tab === 'projects' ? 'active' : ''}`} onClick={() => { setTab('projects'); loadProjects(); }}>
          Projeler ({projects.length})
        </button>

        {selectedCourse && (
          <button className={`courses-tab ${tab === 'lessons' ? 'active' : ''}`} onClick={() => setTab('lessons')}>
            {selectedCourse.title} — Dersler ({lessons.length})
          </button>
        )}
      </div>

      {/* ── KURSLAR ── */}
      {tab === 'courses' && (
        <>
          <button className="lesson-btn primary" style={{ marginBottom: 20 }} onClick={() => {
            setCourseModalMode('create');
            setSelectedCourseId(null);
            setCourseForm({ title: '', description: '', category: 'python', difficulty: 'beginner', age_category: '', cover_image_url: '' });
            setShowCourseModal(true);
          }}>
            Yeni Kurs
          </button>

          {showCourseModal && (
            <div className="admin-modal-backdrop" onClick={() => setShowCourseModal(false)}>
              <div className="admin-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                <h3>{courseModalMode === 'create' ? 'Yeni Kurs Oluştur' : 'Kursu Düzenle'}</h3>
                <div className="form-group">
                  <label>Başlık</label>
                  <input className="input" value={courseForm.title} onChange={e => setCourseForm(c => ({ ...c, title: e.target.value }))} placeholder="Kurs başlığı" required />
                </div>
                <div className="form-group">
                  <label>Açıklama</label>
                  <textarea className="input" rows={3} value={courseForm.description} onChange={e => setCourseForm(c => ({ ...c, description: e.target.value }))} placeholder="Kursun detaylı açıklaması" />
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Kategori</label>
                    <select className="input" value={courseForm.category} onChange={e => setCourseForm(c => ({ ...c, category: e.target.value }))}>
                      {Object.entries(COURSE_CAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zorluk</label>
                    <select className="input" value={courseForm.difficulty} onChange={e => setCourseForm(c => ({ ...c, difficulty: e.target.value }))}>
                      <option value="beginner">Başlangıç</option>
                      <option value="intermediate">Orta</option>
                      <option value="advanced">İleri</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Yaş Kategorisi</label>
                  <select className="input" value={courseForm.age_category} onChange={e => setCourseForm(c => ({ ...c, age_category: e.target.value }))}>
                    <option value="">Genel (Yaş Sınırı Yok)</option>
                    <option value="7-9">7-9 Yaş</option>
                    <option value="10-13">10-13 Yaş</option>
                    <option value="14-17">14-17 Yaş</option>
                  </select>
                </div>

                {/* ── KAPAK GÖRSELİ ── */}
                <div className="form-group">
                  <label>Kapak Görseli</label>
                  {courseForm.cover_image_url && (
                    <div style={{ marginBottom: 8, borderRadius: 10, overflow: 'hidden', border: '2px solid var(--primary)', position: 'relative', height: 120 }}>
                      <img
                        src={courseForm.cover_image_url}
                        alt="Kapak önizlemesi"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        onClick={() => setCourseForm(f => ({ ...f, cover_image_url: '' }))}
                        style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: 14, lineHeight: '24px', textAlign: 'center' }}
                      >✕</button>
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverUpload} />
                  <button
                    type="button"
                    className="lesson-btn secondary"
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imgUploading}
                  >
                    {imgUploading ? (
                      <>
                        <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                        Yükleniyor... %{imgUploadProgress}
                      </>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Image size={14} />
                        {courseForm.cover_image_url ? 'Görseli Değiştir' : 'Görsel Seç'}
                      </span>
                    )}
                  </button>
                </div>

                <div className="admin-modal-actions">
                  <button className="lesson-btn secondary" onClick={() => setShowCourseModal(false)}>İptal</button>
                  <button className="lesson-btn primary" onClick={saveCourse} disabled={imgUploading}>
                    {courseModalMode === 'create' ? 'Oluştur' : 'Güncelle'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isLoading ? <div className="courses-loading">Yükleniyor...</div> : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Kurs</th>
                    <th>Kategori</th>
                    <th>Yaş Grubu</th>
                    <th>Zorluk</th>
                    <th>Yayında</th>
                    <th>Premium</th>
                    <th>Sertifika</th>
                    <th>Hackathon</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {c.cover_image_url ? (
                            <img src={c.cover_image_url} alt={c.title} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1.5px solid var(--border)', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 44, height: 44, borderRadius: 8, background: 'var(--bg-alt)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', flexShrink: 0 }}>
                              <Book size={20} />
                            </div>
                          )}
                          <span style={{ fontWeight: 600 }}>{c.title}</span>
                        </div>
                      </td>
                      <td>{CAT_LABELS[c.category] ?? c.category}</td>
                      <td>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          background: c.age_category ? 'var(--primary-soft)' : 'var(--border)',
                          color: c.age_category ? 'var(--primary-dark)' : 'var(--text-secondary)',
                          padding: '3px 8px',
                          borderRadius: '12px'
                        }}>
                          {c.age_category ? `${c.age_category} Yaş` : 'Genel'}
                        </span>
                      </td>
                      <td>{DIFF_LABELS[c.difficulty] ?? c.difficulty}</td>
                      <td><ToggleBtn active={c.is_published} onToggle={() => patchCourse(c.id, { is_published: !c.is_published })} labels={['Yayında', 'Taslak']} colors={['var(--secondary)', 'var(--gray)']} /></td>
                      <td><ToggleBtn active={c.is_premium} onToggle={() => patchCourse(c.id, { is_premium: !c.is_premium })} labels={['Premium', 'Ücretsiz']} colors={['var(--accent)', 'var(--gray)']} /></td>
                      <td><ToggleBtn active={c.certificate_enabled} onToggle={() => patchCourse(c.id, { certificate_enabled: !c.certificate_enabled })} labels={['Açık', 'Kapalı']} colors={['var(--primary-light)', 'var(--gray)']} /></td>
                      <td><ToggleBtn active={c.hackathon_enabled} onToggle={() => patchCourse(c.id, { hackathon_enabled: !c.hackathon_enabled })} labels={['Açık', 'Kapalı']} colors={['var(--amber,#F59E0B)', 'var(--gray)']} /></td>
                      <td style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button className="lesson-btn secondary sm" style={{ padding: '4px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => {
                          setCourseModalMode('edit');
                          setSelectedCourseId(c.id);
                          setCourseForm({
                            title: c.title || '',
                            description: c.description || '',
                            category: c.category || 'python',
                            difficulty: c.difficulty || 'beginner',
                            age_category: c.age_category || '',
                            cover_image_url: c.cover_image_url || '',
                          });
                          setShowCourseModal(true);
                        }}>
                          <Edit size={12} /> Düzenle
                        </button>
                        <button className="lesson-btn secondary sm" style={{ padding: '4px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => openLessons(c)}>
                          <Book size={12} /> Dersler
                        </button>
                        <button className="admin-remove-btn" onClick={() => deleteCourse(c.id, c.title)}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── PROJELER (ADMIN PROJE YÖNETİMİ) ── */}
      {tab === 'projects' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 18, margin: 0 }}>Kullanıcı Projeleri Yönetimi</h2>
              <p style={{ fontSize: 13, color: 'var(--gray)', margin: '4px 0 0 0' }}>
                Burada eklenen projeler "Projelerim" sayfasında kategorilerine göre kullanıcılara görünür.
              </p>
            </div>
            <button className="lesson-btn primary" onClick={handleNewProject}>
              Yeni Proje Ekle
            </button>
          </div>

          {isLoading ? <div className="courses-loading">Yükleniyor...</div> : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Proje Başlığı</th>
                    <th>Kategori</th>
                    <th>Zorluk</th>
                    <th>XP Ödülü</th>
                    <th>Tahmini Süre</th>
                    <th>Yayın Durumu</th>
                    <th>Premium</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray)', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.description || 'Açıklama yok'}
                        </div>
                      </td>
                      <td>{CAT_LABELS[p.category || 'coding'] ?? p.category ?? 'Kodlama'}</td>
                      <td>{DIFF_LABELS[p.difficulty] ?? p.difficulty}</td>
                      <td style={{ color: 'var(--accent)', fontWeight: 700 }}>+{p.xp_reward ?? 100} XP</td>
                      <td>{p.estimated_duration || '2 Saat'}</td>
                      <td>
                        <ToggleBtn
                          active={p.is_published !== false}
                          onToggle={() => patchProject(p.id, { is_published: p.is_published === false })}
                          labels={['Yayında', 'Taslak']}
                          colors={['var(--secondary)', 'var(--gray)']}
                        />
                      </td>
                      <td>
                        <ToggleBtn
                          active={p.is_premium === true}
                          onToggle={() => patchProject(p.id, { is_premium: !p.is_premium })}
                          labels={['Açık', 'Kapalı']}
                          colors={['var(--accent)', 'var(--gray)']}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button className="lesson-btn secondary sm" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => handleEditProject(p)}>
                            Düzenle
                          </button>
                          <button className="admin-remove-btn" onClick={() => deleteProject(p.id, p.title)}>
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray)', padding: 24 }}>
                        Henüz oluşturulmuş proje bulunmuyor. "Yeni Proje Ekle" butonunu kullanarak yeni proje ekleyebilirsiniz.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── DERSLER ── */}
      {tab === 'lessons' && selectedCourse && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
            <button className="lesson-btn secondary sm" onClick={() => setTab('courses')}>
              Kurslara Dön
            </button>
            <button className="lesson-btn primary sm" onClick={handleNewLesson}>
              Yeni Ders Ekle
            </button>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>#</th><th>Ders</th><th>XP</th><th>Önizleme</th><th>Premium</th><th>Video</th><th>Quiz</th><th>Editör</th><th>İşlemler</th></tr>
              </thead>
              <tbody>
                {lessons.map((l) => (
                  <tr key={l.id}>
                    <td style={{ color: 'var(--gray)', fontSize: 12 }}>{l.order_index}</td>
                    <td style={{ fontWeight: 600 }}>{l.title}</td>
                    <td style={{ color: 'var(--accent)' }}>+{l.xp_reward} XP</td>
                    <td><ToggleBtn active={l.is_preview} onToggle={() => patchLesson(l.id, { is_preview: !l.is_preview })} labels={['Açık', 'Kapalı']} colors={['var(--secondary)', 'var(--gray)']} /></td>
                    <td><ToggleBtn active={l.is_premium} onToggle={() => patchLesson(l.id, { is_premium: !l.is_premium })} labels={['Premium', 'Ücretsiz']} colors={['var(--accent)', 'var(--gray)']} /></td>
                    <td><ToggleBtn active={l.has_video} onToggle={() => patchLesson(l.id, { has_video: !l.has_video })} labels={['Evet', 'Hayır']} colors={['var(--primary-light)', 'var(--gray)']} /></td>
                    <td><ToggleBtn active={l.has_quiz} onToggle={() => patchLesson(l.id, { has_quiz: !l.has_quiz })} labels={['Evet', 'Hayır']} colors={['var(--primary-light)', 'var(--gray)']} /></td>
                    <td><ToggleBtn active={l.has_editor} onToggle={() => patchLesson(l.id, { has_editor: !l.has_editor })} labels={['Evet', 'Hayır']} colors={['var(--primary-light)', 'var(--gray)']} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button className="lesson-btn secondary sm" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => handleEditLesson(l)}>Düzenle</button>
                        <button className="lesson-btn primary sm" style={{ padding: '2px 6px', fontSize: 11, background: 'var(--primary-light)' }} onClick={() => handleOpenTopics(l)}>Konular</button>
                        <button className="admin-remove-btn" onClick={() => deleteLesson(l.id, l.title)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {lessons.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray)', padding: 24 }}>Ders yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Proje Ekleme / Düzenleme Modalı */}
      {showProjectModal && (
        <ProjectFormModal
          project={selectedProjectForEdit}
          onClose={() => { setShowProjectModal(false); setSelectedProjectForEdit(null); }}
          onSaved={() => loadProjects()}
        />
      )}

      {/* Ders Ekleme/Düzenleme Modalı */}
      {showLessonForm && selectedCourse && (
        <LessonFormModal
          courseId={selectedCourse.id}
          lesson={selectedLessonForEdit as any}
          onClose={() => { setShowLessonForm(false); setSelectedLessonForEdit(null); }}
          onSaved={() => loadLessons(selectedCourse.id)}
        />
      )}

      {/* Ders Konuları Yönetim Modalı */}
      {showTopicsPanel && selectedLessonForTopics && (
        <LessonTopicsPanel
          lesson={selectedLessonForTopics as any}
          onClose={() => { setShowTopicsPanel(false); setSelectedLessonForTopics(null); }}
        />
      )}
    </div>
  );
}

// Küçük toggle butonu bileşeni
function ToggleBtn({ active, onToggle, labels, colors }: { active: boolean; onToggle: () => void; labels: [string, string]; colors: [string, string] }) {
  return (
    <button
      onClick={onToggle}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontWeight: 700, fontSize: 13,
        color: active ? colors[0] : colors[1],
        padding: '4px 8px',
      }}
    >
      {active ? labels[0] : labels[1]}
    </button>
  );
}
